import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extract } from "../../../client/backend-client/AxiosUtils"
import { useAppContext } from "../../../config/context"
import { useHeaders } from "../../../queries/axios-headers"
import { ApiRessourcesPassees, ControleResourceApiUpdateOrSaveRequest } from "../../../client/backend-client/generated"

export const useGetControls = (noteId: string) => {
  const { controlsResourceApi } = useAppContext()
  const getHeaders = useHeaders()

  const { isLoading, data } = useQuery({
    queryKey: ["controlsComite", noteId],
    refetchOnMount: "always",
    queryFn: async () => {
      const { headers } = await getHeaders()
      return controlsResourceApi.getControles({ noteId }, { headers }).then(extract)
    },
    retry: 0,
    meta: {
      errorMessage: `Erreur lors de la récupération des informations de contrôle de comité`
    }
  })

  return {
    isLoading,
    data
  }
}

export const useGetAllControls = (noteId: string) => {
  const { controlsResourceApi } = useAppContext()
  const getHeaders = useHeaders()

  const { isLoading, data } = useQuery({
    queryKey: ["allControls", noteId],
    refetchOnMount: "always",
    queryFn: async () => {
      const { headers } = await getHeaders()
      return controlsResourceApi.getAllControlesOk({ noteId }, { headers }).then(extract)
    },
    retry: 0,
    meta: {
      errorMessage: `Erreur lors de la récupération des informations de contrôle de comité`
    }
  })

  return {
    isLoading,
    data
  }
}

export const useGetInfoEncadrementRemboursementPretFAI = (projectId: number, noteId: string) => {
  const { controlsResourceApi } = useAppContext()
  const getHeaders = useHeaders()

  const { isLoading, data, error } = useQuery({
    queryKey: ["getInfoEncadrementRemboursementPretFAI", projectId, noteId],
    refetchOnMount: "always",
    queryFn: async () => {
      const { headers } = await getHeaders()
      return controlsResourceApi
        .getInfoEncadrementRemboursementPretFAI({ projectId, noteId }, { headers })
        .then(extract)
    },
    retry: 0,
    meta: {
      errorMessage: `Erreur lors de la récupération du projet ${projectId} et de la note ${noteId}`
    }
  })

  return {
    isLoading,
    data,
    error
  }
}

export const useSaveOrUpdateRessourcesPassees = (noteId: string) => {
  const { controlsResourceApi } = useAppContext()
  const getHeaders = useHeaders()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: ControleResourceApiUpdateOrSaveRequest) => {
      const { headers } = await getHeaders()
      return controlsResourceApi.updateOrSave(request, { headers }).then(extract)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["controlsComite", noteId] })
    },
    meta: {
      errorMessage: `Erreur lors de la sauvegarde des ressources passées`
    }
  })
}

export const toRessourcesPasseesRequest = (
  noteId: string,
  ressourcesPassees: ApiRessourcesPassees
): ControleResourceApiUpdateOrSaveRequest => {
  return {
    noteId,
    ressourcesPasseesRequest: {
      apportsPrimesSubventions: ressourcesPassees.apportsPrimesSubventions!,
      prets: ressourcesPassees.prets!,
      isUtiliseControleNote: ressourcesPassees.isUtiliseControleNote,
    }
  }
}